import { useState } from 'react';
import { Paperclip, Download, Eye } from 'lucide-react';
import {
  Button, Skeleton, Badge, Card, CardBody, FilePreviewDialog, IconButton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import { useTicketAttachments } from '@/api/tickets';
import { formatDate } from '@/lib/format';

interface AttachmentsTabProps {
  ticketId: number;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Attachment list for a ticket. Each row shows name, size, uploader, and a
 * download link built from APP_STORAGE + attachment_path.
 */
export function AttachmentsTab({ ticketId }: AttachmentsTabProps) {
  const { data, isLoading, isError, refetch } = useTicketAttachments(ticketId);
  const attachments = data?.results ?? [];
  const storageBase = (import.meta.env.APP_STORAGE as string) ?? '';
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);

  if (isLoading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {[1, 2, 3].map((i) => <Skeleton key={i} style={{ height: 48 }} />)}
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load attachments"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>
        }
      />
    );
  }

  if (attachments.length === 0) {
    return (
      <EmptyState
        icon={<Paperclip size={32} />}
        title="No attachments"
        description="This ticket has no uploaded files."
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <Paperclip size={16} style={{ color: 'var(--vn-fg-muted)' }} />
        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 'var(--vn-font-weight-medium)' }}>
          Attachments
        </span>
        <Badge size="sm" tone="neutral">
          {attachments.length}
        </Badge>
      </div>

      {/* Attachment rows */}
      {attachments.map((att) => {
        const downloadUrl = att.ticket_attachment_path
          ? `${storageBase}/${att.ticket_attachment_path}`
          : null;

        return (
          <Card key={att.ticket_attactment_id} flat>
            <CardBody style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
              <Paperclip size={16} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--vn-font-size-sm)',
                    fontWeight: 'var(--vn-font-weight-medium)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {att.ticket_attactment_name || 'Untitled'}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--vn-space-3)',
                    fontSize: 'var(--vn-font-size-xs)',
                    color: 'var(--vn-fg-muted)',
                    marginTop: 'var(--vn-space-1)',
                    flexWrap: 'wrap',
                  }}
                >
                  {att.ticket_attachment_size ? (
                    <span>{formatBytes(att.ticket_attachment_size)}</span>
                  ) : null}
                  {att.employee_name && (
                    <span>Uploaded by {att.employee_name}</span>
                  )}
                  {att.created_at && (
                    <span>{formatDate(att.created_at)}</span>
                  )}
                </div>
              </div>

              {downloadUrl && (
                <IconButton
                  size="sm"
                  variant="ghost"
                  aria-label={`Preview ${att.ticket_attactment_name || 'attachment'}`}
                  onClick={() =>
                    setPreview({ url: downloadUrl, name: att.ticket_attactment_name || 'Untitled' })
                  }
                >
                  <Eye size={14} />
                </IconButton>
              )}
              {downloadUrl && (
                <Button
                  size="sm"
                  variant="ghost"
                  asChild
                >
                  <a href={downloadUrl} download={att.ticket_attactment_name} target="_blank" rel="noreferrer">
                    <Download size={14} />
                  </a>
                </Button>
              )}
            </CardBody>
          </Card>
        );
      })}
      <FilePreviewDialog
        open={preview != null}
        onOpenChange={(open) => { if (!open) setPreview(null); }}
        file={preview ?? undefined}
        onDownload={
          preview ? () => window.open(preview.url, '_blank', 'noopener,noreferrer') : undefined
        }
      />
    </div>
  );
}
